// Booking Response Structure
import { BookingStatus } from '@/config'

export interface BookingResponse {
  message: {
    userId: string
    total: number
    page: number
    pageSize: number
    bookings: BookingSummary[]
  }
}

// Simplified BookingSummary
export interface BookingSummary {
  reference: string
  extReferenceBO: string
  packageCode: string
  liheId: number
  liheName: string
  stationName: string
  createDate: string | null
  reservedAt: string | null
  activityDate: string | null
  firstIresaSyncDate: string | null
  lastIresaSyncDate: string | null
  departureDate: string
  returnDate: string
  sellingPrice: number
  status: BookingStatus
  travellers: TravellerSummary[]
  options: OptionSummary[]
  payments: PaymentSummary[]
  paymentLink?: string
  quoteLink?: string
  image?: string | null // Keep image fiaeld if needed
  leftToPay: number
  createVersionDate: string // Date when the booking was created/updated
  /** Required initial deposit in cents (from WIYP / booking row). */
  initialPaymentRequired?: number | null
  /** Paid deposit amount in cents. */
  depositPaidCents?: number | null
  /** Remaining deposit in cents (set by booking API `attachDepositFieldsToBooking`). */
  depositRemainingCents?: number | null
  /** True when required deposit has been fully paid. */
  depositMet?: boolean
}

// Nested Structures
export interface TravellerSummary {
  id: number
  firstName: string | null
  lastName: string | null
  birthday: string
}

export interface OptionSummary {
  id: string
  bookingReference: string
  commission: number | null
  endDate: string
  familyId: number
  name: string
  prestId: number
  prestTypeId: number
  providerId: number
  publicPrice: string | null
  purchasePrice: string | null
  quantity: number
  sellingPrice: string | null
  startDate: string
  updateDate: string | null
  createDate: string
  crossSell: any | null
  crossSellAgent: number
  crossSellQuantity: number | null
  promoCodeDiscount: any | null
  discount: any | null
  negativeDiscount: any | null
  providerDiscount: any | null
  accountingDateValidity: any | null
  cancellationUntil: string | null
  accountingReferenceValidity: any | null
  accountingReconciliation: boolean
  travellerIds: string[] | null
  paymentCondition: number | null
  tripInformation?: FlightDetails | TransferDetails | LuggageDetails[] | any; // Optional tripInformation property for flight, transfer, luggage, and train options.
}

export enum PaymentStatus {
  PAID = 'Payé',
}

//Define an enum for payment actions
export enum PaymentAction {
  DEBIT = "DEBIT",
  REFUND = "REFUND",
}

export interface PaymentSummary {
  id: string
  amount: string
  currency: string
  datePayment: string
  mode: string
  status: string
  /** Cart/quote instalment type — `"Payment Link"` for balance-payment rows. */
  payment_action?: string | null
}

export enum PaymentCondition {
  FLEXIBLE = 1,
  FIXED = 2,
}

export interface UpdateTravellersResponse {
  message: {
    errors: number[]
    success: number[]
  }
}

export interface FlightDetails {
  id: string
  class: string
  cacheDate: string
  priceBaby: {
    tax: number
    base: number
    currency: string
  }
  priceAdult: {
    tax: number
    base: number
    currency: string
  }
  carrierType: string
  inboundTrip: {
    class: string
    arrival: {
      date: string
      time: string
      location: string
      arrivalDateOffset: number
    }
    company: string
    duration: number
    stopOver: number
    departure: {
      date: string
      time: string
      location: string
    }
  }
  outboundTrip: {
    class: string
    arrival: {
      date: string
      time: string
      location: string
      arrivalDateOffset: number
    }
    company: string
    duration: number
    stopOver: number
    departure: {
      date: string
      time: string
      location: string
    }
  }
  platingCarrier: string
  sessionJourney: string
}

export interface TransferDetails {
  id: number
  price: number | null
  origin: string
  capacity: number
  priceBaby: number
  originCode: string
  priceAdult: number
  priceChild: number
  returnDate: string
  destination: string
  transferType: string
  departureDate: string
  destinationId: string
  lastCheckDate: string
  transferReturnRef: string | null
  transferDepartureRef: string | null
  outboundHours: TransferOption[] | TaxiTimeRange
  inboundHours: TransferOption[] | TaxiTimeRange
}

export interface TaxiTimeRange {
  from: string
  to: string
  provider: string
}

export interface TransferOption {
  bus: number
  tad: boolean
  infos: string
  route: {
    url: string | null
    desc: string | null
    type: string
    color: string | null
    long_name: string | null
    short_name: string
    text_color: string | null
  } | null
  charge: any | null
  depart: string
  arrivee: string
  nbPlaces: number
  segments: any[]
  totalCo2: number
  seuilVelo: any | null
  stopVente: boolean
  tarifNuit: boolean
  chargeVelo: any | null
  delaisResa: any | null
  departInfo: {
    id: string
    date: string
    name: string
    coords: {
      lat: number
      lng: number
    }
  } | null
  route_type: string | null
  arriveeInfo: {
    id: string
    date: string
    name: string
    coords: {
      lat: number
      lng: number
    }
  } | null
  seuilAlerte: number
  totalShapes: any[]
  seuilBlocage: number
  totalTransit: number
  totalDistance: number
  trip_short_name: string | null
}

export interface LuggageDetails {
  id: string
  name: string
  default: boolean
  details: {
    price: number
    weight: number
    currency: string
    travellers: {
      selected: boolean
      travellerId: string
    }[]
    description: string
    luggageType: string
  }
  endDate: string
  familyId: number
  quantity: number
  mandatory: boolean
  startDate: string
  nominative: boolean
  prestTypeId: number
  sellingPrice: number
  pricePerTraveller: {
    id: string
    price: number
    selected: boolean
  }[]
}
